import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useAuth} from '../../contexts/AuthContext';
import {Alert, Box, Button, CircularProgress, Divider, TextField, Typography} from '@mui/material';
import {Email, Google, Person} from '@mui/icons-material';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInWithPopup,
    updateProfile
} from 'firebase/auth';
import {auth, db, provider} from '../../firebase-config';
import {collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where} from 'firebase/firestore';

// Hilfsfunktion zum Generieren eines URL-freundlichen Strings
const generateSlug = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Entferne alle Sonderzeichen außer Bindestriche
    .replace(/\s+/g, '-') // Ersetze Leerzeichen durch Bindestriche
    .replace(/-+/g, '-'); // Entferne mehrfache Bindestriche
};

// Hilfsfunktion zum Generieren eines einzigartigen Profilpfads
const generateUniqueProfilePath = async (displayName, isGoogleUser = false) => {
  let basePath;

  if (isGoogleUser) {
    // For Google users: Extract first and last name
    const [firstName, ...lastNameParts] = displayName.split(' ');
    const lastName = lastNameParts.join(' ');
    basePath = `g-${generateSlug(firstName)}-${generateSlug(lastName)}`;
  } else {
    // For regular users: Use the username directly
    basePath = generateSlug(displayName);
  }

  // Check if the base path exists
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('profilePath', '==', basePath));
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return basePath;
  }

  // Add numbers to the path until we find a unique one
  const MAX_ATTEMPTS = 1000; // Prevent infinite loops
  for (let counter = 2; counter <= MAX_ATTEMPTS; counter++) {
    const newPath = `${basePath}${counter}`;
    const q = query(usersRef, where('profilePath', '==', newPath));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return newPath;
    }
  }

  // If we somehow reach this point, throw an error
  throw new Error('Could not generate a unique profile path after ${MAX_ATTEMPTS} attempts');
}

export default function LoginForm({ defaultToRegister = false, showRegisterFirst = false, compact = false }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isRegistering, setIsRegistering] = useState(defaultToRegister || showRegisterFirst);
  const navigate = useNavigate();
  const { loginAsGuest, login } = useAuth();

  useEffect(() => {
    setIsRegistering(defaultToRegister || showRegisterFirst);
  }, [defaultToRegister, showRegisterFirst]);

  const validateForm = () => {
    if (isRegistering && !username) {
      setError('Bitte gib einen Benutzernamen ein.');
      return false;
    }
    if (isRegistering && username.length > 20) {
      setError('Der Benutzername darf maximal 20 Zeichen lang sein.');
      return false;
    }
    if (!email || !password) {
      setError('Bitte fülle alle Felder aus.');
      return false;
    }
    if (password.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      return false;
    }
    if (!email.includes('@')) {
      setError('Bitte gib eine gültige E-Mail-Adresse ein.');
      return false;
    }
    return true;
  };

  const createUserDocument = async (user, isGoogleUser = false) => {
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        const profilePath = user.profilePath || await generateUniqueProfilePath(user.displayName, isGoogleUser);
        await setDoc(userRef, {
          displayName: user.displayName,
          email: user.email,
          photoURL: user.photoURL,
          profilePath,
          createdAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
          favoriteQuizzes: [],
          friends: [],
          totalGamesPlayed: 0,
          totalScore: 0
        });
      } else {
        await setDoc(userRef, {
          lastLoginAt: serverTimestamp()
        }, { merge: true });
      }
    } catch (error) {
      console.error('Fehler beim Erstellen/Aktualisieren des Benutzerdokuments:', error);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');
    try {
      if (isRegistering) {
        // 1. Erstelle den Benutzer
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);

        // 2. Setze den displayName
        await updateProfile(userCredential.user, {
          displayName: username
        });

        // 3. Lade den Benutzer neu
        await userCredential.user.reload();

        // 4. Hole den aktualisierten Benutzer
        const updatedUser = auth.currentUser;

        // 5. Erstelle das Firestore-Dokument
        const profilePath = await generateUniqueProfilePath(username, false);
        await createUserDocument({
          ...updatedUser,
          displayName: username,
          profilePath
        }, false);

        // 6. Aktualisiere den currentUser im AuthContext
        const userDoc = await getDoc(doc(db, 'users', updatedUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const enrichedUser = {
            ...updatedUser,
            profilePath: userData.profilePath
          };
          login(enrichedUser);
        }
      } else {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        await createUserDocument(userCredential.user);
      }
    } catch (error) {
      let errorMessage;
      switch (error.code) {
        case 'auth/invalid-email':
          errorMessage = "Die E-Mail-Adresse ist ungültig.";
          break;
        case 'auth/user-disabled':
          errorMessage = "Dieser Account wurde deaktiviert.";
          break;
        case 'auth/user-not-found':
          errorMessage = "Kein Account mit dieser E-Mail-Adresse gefunden.";
          break;
        case 'auth/wrong-password':
          errorMessage = "Falsches Passwort.";
          break;
        case 'auth/invalid-credential':
          errorMessage = "E-Mail oder Passwort ist falsch.";
          break;
        case 'auth/email-already-in-use':
          errorMessage = "Diese E-Mail-Adresse wird bereits verwendet.";
          break;
        case 'auth/weak-password':
          errorMessage = "Das Passwort muss mindestens 6 Zeichen lang sein.";
          break;
        case 'auth/network-request-failed':
          errorMessage = "Netzwerkfehler. Bitte überprüfe deine Internetverbindung.";
          break;
        case 'auth/too-many-requests':
          errorMessage = "Zu viele Versuche. Bitte versuche es später erneut.";
          break;
        default:
          console.error('Auth error:', error);
          errorMessage = "Ein unerwarteter Fehler ist aufgetreten. Bitte versuche es später erneut.";
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await signInWithPopup(auth, provider);

      // Prüfe zuerst, ob der Benutzer bereits existiert
      const userRef = doc(db, 'users', result.user.uid);
      const userDoc = await getDoc(userRef);

      let profilePath;
      if (userDoc.exists()) {
        // Benutzer existiert bereits, verwende den vorhandenen profilePath
        profilePath = userDoc.data().profilePath;
      } else {
        // Neuer Benutzer, generiere einen neuen profilePath
        profilePath = await generateUniqueProfilePath(result.user.displayName, true);
      }

      // Erstelle/Aktualisiere das Firestore-Dokument
      await createUserDocument({
        ...result.user,
        profilePath
      }, true);

      // Aktualisiere den AuthContext mit dem korrekten profilePath
      const enrichedUser = {
        ...result.user,
        profilePath
      };
      login(enrichedUser);
    } catch (error) {
      if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
        return;
      }
      console.error('Google Login error:', error);
      setError('Ein Fehler ist bei der Anmeldung aufgetreten.');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setLoading(true);
    setError('');
    try {
      await loginAsGuest();
      navigate('/');
    } catch (error) {
      console.error('Gast-Login fehlgeschlagen:', error);
      setError('Gast-Login fehlgeschlagen. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <form onSubmit={handleEmailLogin} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <TextField
            size="small"
            label="E-Mail"
            variant="outlined"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoComplete="email"
            InputLabelProps={{
              shrink: true,
              disableAnimation: true
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                height: '1.2rem'
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem'
              }
            }}
          />
          <TextField
            size="small"
            label="Passwort"
            variant="outlined"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoComplete="current-password"
            InputLabelProps={{
              shrink: true,
              disableAnimation: true
            }}
            sx={{
              '& .MuiInputBase-input': {
                fontSize: '0.875rem',
                height: '1.2rem'
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem'
              }
            }}
          />
          <Button
            variant="contained"
            type="submit"
            disabled={loading}
            size="small"
            startIcon={loading ? <CircularProgress size={16} /> : <Email />}
          >
            {loading ? "..." : "Anmelden"}
          </Button>
        </form>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 400, mx: 'auto' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleEmailLogin}>
        {isRegistering && (
          <TextField
            fullWidth
            label="Benutzername"
            variant="outlined"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={loading}
            autoComplete="username"
            InputLabelProps={{
              shrink: true,
              disableAnimation: true
            }}
            sx={{ mb: 2 }}
          />
        )}
        <TextField
          fullWidth
          label="E-Mail"
          variant="outlined"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
          autoComplete="email"
          InputLabelProps={{
            shrink: true,
            disableAnimation: true
          }}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Passwort"
          variant="outlined"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={loading}
          autoComplete={isRegistering ? "new-password" : "current-password"}
          InputLabelProps={{
            shrink: true,
            disableAnimation: true
          }}
          sx={{ mb: 2 }}
        />
        <Button
          fullWidth
          variant="contained"
          type="submit"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <Email />}
          sx={{ mb: 2 }}
        >
          {loading ? "Wird ausgeführt..." : (isRegistering ? "Registrieren" : "Anmelden")}
        </Button>
      </form>

      {!defaultToRegister && (
        <Typography
          variant="body2"
          sx={{ textAlign: 'center', mb: 2, cursor: 'pointer', color: 'primary.main' }}
          onClick={() => setIsRegistering(!isRegistering)}
        >
          {isRegistering ? "Bereits registriert? Hier anmelden" : "Noch kein Account? Hier registrieren"}
        </Typography>
      )}

      <Divider sx={{ my: 2 }}>oder</Divider>

      <Button
        fullWidth
        variant="contained"
        onClick={handleGoogleLogin}
        disabled={loading}
        startIcon={<Google />}
        className="google-login-button"
        sx={{ mb: 2 }}
      >
        Mit Google anmelden
      </Button>

      <Button
        fullWidth
        variant="outlined"
        onClick={handleGuestLogin}
        disabled={loading}
        startIcon={<Person />}
        sx={{
          borderColor: 'rgba(255, 255, 255, 0.2)',
          color: 'text.secondary',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
            bgcolor: 'rgba(255, 255, 255, 0.05)'
          }
        }}
      >
        Als Gast fortfahren
      </Button>
    </Box>
  );
}